// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSpinner,
    faCheck,
    faExclamationTriangle,
    faXmark,
    faChevronUp,
    faChevronDown
} from '@fortawesome/free-solid-svg-icons';

interface YouTubeProgressToastProps {
    status: {
        is_processing: boolean;
        processing_count: number;
        queue: {
            total: number;
            completed: number;
            processing: number;
            failed: number;
        };
    } | null;
    onClose: () => void;
}

const YouTubeProgressToast: React.FC<YouTubeProgressToastProps> = ({ status, onClose }) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (status && status.is_processing) {
            setIsVisible(true);
        } else if (status && !status.is_processing && status.queue.total > 0) {
            // Keep visible for a few seconds after completion
            setTimeout(() => {
                setIsVisible(false);
                onClose();
            }, 5000);
        }
    }, [status, onClose]);

    if (!status || !isVisible) return null;

    const progress = status.queue.total > 0 
        ? Math.round((status.queue.completed / status.queue.total) * 100)
        : 0;

    const hasErrors = status.queue.failed > 0;
    const isComplete = !status.is_processing && status.queue.completed === status.queue.total;

    return (
        <div className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${
            isMinimized ? 'w-64' : 'w-96'
        }`}>
            <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className={`flex items-center justify-between p-3 ${
                    isComplete ? 'bg-green-50' : hasErrors ? 'bg-yellow-50' : 'bg-blue-50'
                }`}>
                    <div className="flex items-center gap-2">
                        {isComplete ? (
                            <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                        ) : (
                            <FontAwesomeIcon icon={faSpinner} className="text-blue-600 animate-spin" />
                        )}
                        <span className="font-medium text-sm text-gray-800">
                            {isComplete ? 'Processing Complete' : 'Processing YouTube Videos'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsMinimized(!isMinimized)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                        >
                            <FontAwesomeIcon 
                                icon={isMinimized ? faChevronUp : faChevronDown} 
                                className="text-sm"
                            />
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 p-1"
                        >
                            <FontAwesomeIcon icon={faXmark} className="text-sm" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                {!isMinimized && (
                    <div className="p-4">
                        {/* Progress Bar */}
                        <div className="mb-3">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>{status.queue.completed} of {status.queue.total} videos</span>
                                <span>{progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                    className={`h-2 rounded-full transition-all duration-500 ${
                                        isComplete ? 'bg-green-500' : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center justify-between text-xs">
                            {status.queue.processing > 0 && (
                                <div className="flex items-center gap-1 text-blue-600">
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    <span>{status.queue.processing} processing</span>
                                </div>
                            )}
                            
                            {status.queue.completed > 0 && (
                                <div className="flex items-center gap-1 text-green-600">
                                    <FontAwesomeIcon icon={faCheck} />
                                    <span>{status.queue.completed} completed</span>
                                </div>
                            )}
                            
                            {status.queue.failed > 0 && (
                                <div className="flex items-center gap-1 text-red-600">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    <span>{status.queue.failed} failed</span>
                                </div>
                            )}
                        </div>

                        {/* Status Message */}
                        {status.is_processing && (
                            <div className="mt-2 text-xs text-gray-600">
                                Processing videos in the background...
                            </div>
                        )}

                        {isComplete && !hasErrors && (
                            <div className="mt-2 text-xs text-green-600 font-medium">
                                All videos processed successfully!
                            </div>
                        )}

                        {isComplete && hasErrors && (
                            <div className="mt-2 text-xs text-yellow-600 font-medium">
                                Processing complete with {status.queue.failed} errors
                            </div>
                        )}
                    </div>
                )}

                {/* Minimized View */}
                {isMinimized && (
                    <div className="px-3 py-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">
                                {status.queue.completed}/{status.queue.total} videos
                            </span>
                            <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                <div 
                                    className={`h-1.5 rounded-full ${
                                        isComplete ? 'bg-green-500' : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default YouTubeProgressToast;